import { createReactExtension } from '@backstage/core-plugin-api';
import { CustomCarousel } from '@internal/ui-common';
import { homePlugin } from '@backstage/plugin-home';
import useStyles from './NewsAndUpdates.styles';
import WrenchIcon from '@mui/icons-material/Build';

import { Link, Typography } from '@material-ui/core';
import {
  LinkButton,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { NewsUpdate, useNewsUpdates } from './hooks/useNewsUpdates';
import { useEffect, useState } from 'react';

interface NewsAndUpdatesTitleProps {
  classes: {
    title: string;
    icon: string;
  };
}

interface NewsAndUpdatesContentProps {
  newsUpdates: NewsUpdate[];
  overflowingIds: string[];
  classes: {
    description: string;
    overflow: string;
  };
}

const NewsAndUpdatesTitle: React.FC<NewsAndUpdatesTitleProps> = ({
  classes,
}) => (
  <Typography variant="h4" className={classes.title}>
    <WrenchIcon className={classes.icon} />
    News and updates
  </Typography>
);

const NewsAndUpdatesContent: React.FC<NewsAndUpdatesContentProps> = ({
  newsUpdates,
  overflowingIds,
  classes,
}) => {
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  if (!Array.isArray(newsUpdates) || !newsUpdates.length) {
    return (
      <section>
        <Typography variant="h6" data-testid="no-news-updates-text">
          No news updates yet...
        </Typography>
      </section>
    );
  }

  const parseMarkdownLinks = (text: string) => {
    const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;

    text.replace(linkRegex, (match, label, url, offset) => {
      parts.push(text.substring(lastIndex, offset));
      parts.push(
        <Link
          key={url}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          underline="always"
        >
          {label}
        </Link>,
      );
      lastIndex = offset + match.length;
      return match;
    });

    parts.push(text.substring(lastIndex));
    return parts;
  };

  return (
    <article>
      <CustomCarousel>
        {newsUpdates.map(newsUpdate => (
          <article key={newsUpdate.id}>
            <div>
              <Typography variant="h6">{newsUpdate.title}</Typography>
              <Typography variant="subtitle2">
                {formatDate(newsUpdate.created_at)} -{' '}
                {newsUpdate.category ?? 'Uncategorized'}
              </Typography>
            </div>
            <Typography
              variant="body1"
              className={`${classes.description} ${
                overflowingIds.includes(newsUpdate.id) ? classes.overflow : ''
              }`}
              id={newsUpdate.id}
              data-testid={`news-update-body-${newsUpdate.id}`}
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {parseMarkdownLinks(newsUpdate.body)}
            </Typography>
            <div>
              <LinkButton
                to={`/announcements/view/${newsUpdate.id}`}
                color="primary"
                variant="contained"
              >
                Read more
              </LinkButton>
            </div>
          </article>
        ))}
      </CustomCarousel>
    </article>
  );
};

export const NewsAndUpdates = () => {
  const classes = useStyles();
  const {
    isLoading,
    newsUpdates = [] as NewsUpdate[],
    error,
  } = useNewsUpdates();
  const [overflowingIds, setOverflowingIds] = useState<string[]>([]);

  useEffect(() => {
    if (Array.isArray(newsUpdates)) {
      const ids = newsUpdates
        .filter(newsUpdate => {
          const element = document.getElementById(newsUpdate.id);
          return element && element.scrollHeight > element.clientHeight;
        })
        .map(newsUpdate => newsUpdate.id);
      setOverflowingIds(ids);
    }
  }, [newsUpdates]);

  if (isLoading) {
    return (
      <article
        className={classes.sliderSection}
        data-testid="news-updates-loading"
      >
        <NewsAndUpdatesTitle classes={classes} />
        <Progress />
      </article>
    );
  }

  if (error) {
    return (
      <ResponseErrorPanel error={error} data-testid="news-updates-error" />
    );
  }

  return (
    <article className={classes.sliderSection}>
      <NewsAndUpdatesTitle classes={classes} />
      <NewsAndUpdatesContent
        newsUpdates={newsUpdates || []}
        overflowingIds={overflowingIds}
        classes={classes}
      />
    </article>
  );
};

export const NewsAndUpdatesWidget = homePlugin.provide(
  createReactExtension({
    name: 'News and updates',
    component: {
      lazy: () => import('./NewsAndUpdates').then(m => m.NewsAndUpdates),
    },
    data: {
      'home.widget.config': {
        layout: {
          width: { defaultColumns: 12, minColumns: 6 },
          height: { defaultRows: 5, minRows: 5, maxRows: 5 },
        },
      },
    },
  }),
);
